import { setProgress, setLiveData, resetProgress, setNeedsRefetch } from "@/redux/reducers/InterviewReducer";
import { CandidateService } from "@/services/candidateService";
import toast from "react-hot-toast";
import { SENIORITY_REQUIRED_TYPES } from "../constants/interviewTypes";

const abortMap = {};


export const regenerateInterview = (candidateId, applicationId, hasQuestions, questionType, seniorityLevel) => async (dispatch) => {
  if (!applicationId) return;

  const abortKey = `${applicationId}:${questionType}`;
  abortMap[abortKey]?.abort();
  const controller = new AbortController();
  abortMap[abortKey] = controller;

  dispatch(
    setProgress({
      applicationId,
      questionType,
      progress: { percentage: 0, message: "Initializing generation..." },
    }),
  );

  dispatch(setLiveData({ applicationId, questionType, data: null }));

  let buffer = "";
  let gotConflict = false;

  const processLine = (line) => {
    if (!line.startsWith("data:")) return;

    try {
      const parsed = JSON.parse(line.slice(5));

      if (parsed.type === "progress" || parsed.type === "progress_llm_start" || parsed.type === "progress_llm_end") {
        dispatch(
          setProgress({
            applicationId,
            questionType,
            progress: {
              percentage: parsed.percentage ?? 0,
              message: parsed.message || "Generating...",
            },
          }),
        );
      }

      if (parsed.type === "progress_complete") {
        dispatch(
          setProgress({
            applicationId,
            questionType,
            progress: { percentage: 100, message: "Finalizing..." },
          }),
        );
      }

      if (parsed.type === "file_complete") {
        const incomingType = parsed.question_type ?? questionType;
        const qd = parsed.questions_data || {};
        dispatch(
          setLiveData({
            applicationId,
            questionType: incomingType,
            data: {
              questions: qd.questions ?? [],
              competencies: qd.competencies ?? [],
              mappings: qd.mappings ?? [],
            },
          }),
        );

        dispatch(
          setNeedsRefetch({
            applicationId,
            value: true,
          }),
        );
      }
    } catch {}
  };

  try {
    const level = SENIORITY_REQUIRED_TYPES.includes(questionType) ? seniorityLevel : undefined;
    await CandidateService.generateInterviewQuestions(candidateId, applicationId, questionType, level, {
      signal: controller.signal,
      // Generation is streamed over SSE and remains cancellable through the controller.
      timeout: 0,

      onDownloadProgress: (progressEvent) => {
        const chunk = progressEvent.event?.target?.responseText || "";
        buffer += chunk.slice(buffer.length);

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) processLine(line);
      },
    });

    // ponytail: last SSE line has no trailing "\n" so onDownloadProgress never flushes it
    if (buffer) processLine(buffer);
  } catch (err) {
    let errorCode;
    const body = err?.response?.data;
    if (typeof body === "string") {
      try {
        errorCode = JSON.parse(body)?.error_code;
      } catch {}
    } else if (body) {
      errorCode = body.error_code;
    }

    if (err?.response?.status === 409 && errorCode === "GENERATION_IN_PROGRESS") {
      gotConflict = true;
    } else if (err?.name !== "AbortError") {
      const label = questionType
        ? questionType
            .toLowerCase()
            .replace(/_/g, " ")
            .replace(/\b\w/g, (c) => c.toUpperCase())
        : "Interview";
      if (errorCode === "INTERVIEW_QUESTION_TYPE_NOT_ACTIVE") {
        toast.error(`${label} questions can no longer be generated — this question type was deactivated for the job.`);
        dispatch(setNeedsRefetch({ applicationId, value: true }));
      } else {
        toast.error(`Failed to generate ${label} questions.`);
      }
    }
  } finally {
    if (!gotConflict) {
      dispatch(resetProgress({ applicationId, questionType }));
    }
    delete abortMap[abortKey];
  }
};
